import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { ApiFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-filter-transformer';
import { Editor } from '@tiptap/core';
import { Transaction } from '@tiptap/pm/state';
import { CustomSectionNode } from 'api/custom-section/types';
import { CustomSectionType } from 'api/custom-section/types/common';
import {
  BasePageTemplateUiState,
  PageType,
  SourceType,
  TiptapJson,
} from 'api/page-template/types';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { DocumentEditor } from 'components/organisms/document-editor/document-editor';
import { RlCustomSectionName } from 'components/organisms/document-editor/extensions/custom-section/extension';
import { useCommandsForCommandPickerAsPromise } from './command-for-command-picker';
import { getOperations } from './operations-from-json';
import { TemplateConditions } from './template-conditions';

const CenterWrapper = styled.div`
  width: calc(654px + calc(var(--spacing-06) * 2));
  padding: var(--spacing-05) var(--spacing-06) 0;
  margin: 0px auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;

const EditorWrapper = styled.div`
  height: 100%;
  padding: var(--spacing-10) 0;
  overflow-y: auto;

  .editor-wrapper {
    display: flex;
    justify-content: center;

    .rte-editor {
      flex-grow: 0;
    }
  }

  /* Custom section specific styles */
  .custom-section {
    position: relative;
    margin: 1em 0;
    /* border: 1px solid var(--border-color, #e0e0e0); */
    transition: all 0.2s ease;
  }
`;

const MeetingPageTemplateContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const TemplateConditionWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export interface BaseTemplateProps<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> {
  pageType: PageTypeT;
  templateData?: BasePageTemplateUiState<PageTypeT, SourceTypeT>;
  onTemplateChange: (
    payload: Partial<BasePageTemplateUiState<PageTypeT, SourceTypeT>>,
  ) => void;
  defaultContent: any;
  groupedFields?: GroupedOptions<FilterableField>[];
  groupedTransformer?: ApiFilterTransformer<GroupedFilter>;
  supportFilter?: boolean;
  isModuleFed?: boolean;
  previousTiptapJson?: TiptapJson;
  /**
   * When set, drives TemplateConditions read-only (e.g. federated meeting template
   * with `!canWrite`). When omitted, falls back to `isModuleFed && filtersExist`.
   */
  readOnlyTemplateConditions?: boolean;
}

export const BaseTemplatePage = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>({
  pageType,
  templateData,
  groupedFields,
  groupedTransformer,
  onTemplateChange,
  defaultContent,
  supportFilter = true,
  isModuleFed = false,
  previousTiptapJson,
  readOnlyTemplateConditions: readOnlyTemplateConditionsProp,
}: BaseTemplateProps<PageTypeT, SourceTypeT>) => {
  const filtersExist = Boolean(
    templateData?.filters?.pageTriggerCondition?.groupCriteria?.length,
  );
  const showTemplateConditions = supportFilter || filtersExist;

  const readOnlyTemplateConditions =
    readOnlyTemplateConditionsProp !== undefined
      ? readOnlyTemplateConditionsProp
      : isModuleFed && filtersExist;
  const [changedSections, setChangedSections] = useState<CustomSectionNode[]>(
    templateData?.changedSections ?? [],
  );

  const [documentJson, setDocumentJson] = useState(
    templateData?.tiptapJson ?? defaultContent,
  );

  const emptyDoc: TiptapJson = { type: 'doc', content: [] };
  const previousJson = previousTiptapJson ?? emptyDoc;

  const handleInitialize = (editor: Editor) => {
    if (templateData?.tiptapJson) {
      onTemplateChange({
        htmlPayload: editor.getHTML(),
      });
    }
  };

  const handleChange = (
    value: any,
    editor: Editor,
    transaction: Transaction,
  ) => {
    if (transaction.docChanged) {
      const finalDoc = transaction.doc;
      // Get modified node from transaction steps
      const step = transaction.steps[0];

      if (step && 'slice' in step) {
        // Find the node in the final document using descendants
        const slice = step.slice as { content?: { toJSON(): any[] } };
        const modifiedNode = slice?.content
          ?.toJSON()
          ?.find((node) => node.type === RlCustomSectionName);

        if (!modifiedNode) {
          const pos = 'from' in step ? step.from : 0; // position where change was applied
          const $pos = finalDoc.resolve(pos as number);

          // Walk up the tree until you hit a section node
          for (let d = $pos.depth; d > 0; d--) {
            const parent = $pos.node(d);

            if (parent.type?.name === RlCustomSectionName) {
              handleSectionChange({
                ...parent.toJSON(),
                type: RlCustomSectionName,
              });
            }
          }
        } else if (
          [
            CustomSectionType.TableDetails,
            CustomSectionType.RecordBlock,
          ].includes(modifiedNode?.attrs?.sectionType)
        ) {
          let foundNode: any = null;

          finalDoc.descendants((node, pos) => {
            if (
              node.type.name === RlCustomSectionName &&
              modifiedNode?.attrs?.templateSectionId ===
                node.attrs?.templateSectionId
            ) {
              foundNode = { ...node, pos };
            }
          });
          if (foundNode) {
            handleSectionChange({
              ...foundNode,
              type: RlCustomSectionName,
            });
          }
        } else {
          if (modifiedNode) {
            handleSectionChange(modifiedNode);
          }
        }
      }
      setDocumentJson(value);
      const currentJson = editor.getJSON();

      const operations = getOperations(currentJson, previousJson);

      onTemplateChange({
        tiptapJson: currentJson,
        htmlPayload: editor.getHTML(),
        operations,
      });
    }
  };

  const handleSectionChange = (section: CustomSectionNode) => {
    let updatedSectionsData = [...changedSections];

    setChangedSections((prevSections) => {
      const sectionsWithoutCurrentSection = prevSections.filter(
        (prevSection) =>
          prevSection.attrs?.templateSectionId !==
          section.attrs?.templateSectionId,
      );

      updatedSectionsData = [...sectionsWithoutCurrentSection, section];

      return updatedSectionsData;
    });

    onTemplateChange({
      changedSections: updatedSectionsData,
    });
  };

  const commandsForCommandPickerGetter = useCommandsForCommandPickerAsPromise({
    pageType: pageType,
  });

  const editorExtensionsConfig = useMemo(() => {
    return {
      customSectionConfig: {
        allowResolve: false as const,
        generateTemplateSectionId: true,
        pageType: pageType,
      },
      groupSectionConfig: {
        generateTemplateSectionId: true,
      },
      commandPickerWithTriggerConfig: {
        enabled: true as const,
        items: commandsForCommandPickerGetter,
      },
      endOfDocumentConfig: {
        enabled: true,
      },
      likeDislikeConfig: {
        enabled: false,
      },
      tableToolbarConfig: {
        enabled: true,
      },
    };
  }, [commandsForCommandPickerGetter, pageType]);

  const handleTemplateConditionsChange = (filter: GroupedFilter) => {
    onTemplateChange({
      filters: {
        pageTriggerCondition: filter,
      },
    });
  };

  return (
    <MeetingPageTemplateContainer>
      <EditorWrapper className="document-editor-wrapper">
        <CenterWrapper>
          {showTemplateConditions ? (
            <TemplateConditionWrapper>
              <TemplateConditions
                key={
                  templateData?.filters?.pageTriggerCondition?.groupCriteria
                    ?.length
                }
                apiFilter={templateData?.filters.pageTriggerCondition ?? null}
                groupedFields={groupedFields ?? []}
                pageType={pageType}
                handleTemplateConditionsChange={handleTemplateConditionsChange}
                groupedTransformer={groupedTransformer}
                readOnly={readOnlyTemplateConditions}
              />
            </TemplateConditionWrapper>
          ) : null}

          <DocumentEditor
            value={documentJson}
            onChange={handleChange}
            className="editor-wrapper"
            onInitialize={handleInitialize}
            extensionConfig={editorExtensionsConfig}
            canDragAndDrop={true}
            isInsertOnlyMode={true}
            readonly={true}
          />
        </CenterWrapper>
      </EditorWrapper>
    </MeetingPageTemplateContainer>
  );
};
